/**
 * AddToPipelineDialogContainer — data + selection state for the reusable
 * pick-pipeline-and-stage dialog (SE-5499).
 *
 * This is the board-AGNOSTIC add path (the profile entry point today, the
 * Split-PRD toolbar tomorrow). It owns nothing that ties it to an open board:
 *   - `boardId` absent → the manager picks a pipeline first, then a stage.
 *     (Path B — the profile call site.)
 *   - `boardId` present → the board picker is hidden; only the stage is chosen.
 *     (Path A — a future on-board trigger. NO rebuild required — this is the
 *     AC5 reusability guarantee, proven structurally by the `boardId` prop.)
 *
 * On confirm it fires the shared `useAddPlayerToPipeline` mutation (refetch, not
 * optimism) and, on success, invokes `onAdded` and closes. Fresh selection state
 * per open is guaranteed by the call sites' key flip (the repo's sanctioned
 * alternative to a set-state-in-effect reset).
 *
 * SE-8818: a player profile places this dialog in several spots (the desktop
 * hero cluster or the mobile FAB, plus the Reports & Notes promote action), and
 * it used to fetch the board list on mount, open or not. Every read now skips
 * while closed and a closed dialog renders nothing, so a closed placement sends
 * no request and mounts no modal.
 * @remarks Container component — owns queries, selection state, and the mutation.
 */

import { memo, useCallback, useMemo, useState } from "react";

import {
  KanbanSourceType,
  useAddToPipelineDialogBoardsQuery,
  useListKanbanPhasesQuery,
} from "@/generated/graphql";

import { REVIEW_QUEUE_PAGE_SIZE } from "../../../constants";
import useAddPlayerToPipeline from "../../../hooks/useAddPlayerToPipeline";
import AddToPipelineDialogView, {
  type PipelineBoardOption,
  type PipelineStageOption,
} from "./AddToPipelineDialogView";

/** Public props for the dialog. */
export interface AddToPipelineDialogContainerProps {
  /** Whether the dialog is open. */
  readonly isOpen: boolean;
  /** Close request handler. */
  readonly onClose: () => void;
  /** Player to add. */
  readonly playerId: string;
  /** Optional player display name (title subtitle + accessibility). */
  readonly playerName?: string;
  /**
   * Pre-selected pipeline board. When provided the board picker is hidden and
   * the dialog opens straight to stage selection (Path A). Absent = profile
   * flow where the manager picks the board too (Path B).
   */
  readonly boardId?: string;
  /** Invoked after a successful add (before the dialog closes). */
  readonly onAdded?: () => void;
}

/** Page size for the pipeline board dropdown (reuse the review-queue size). */
const BOARDS_PAGE_SIZE = REVIEW_QUEUE_PAGE_SIZE;

/**
 * Container for the pick-pipeline-and-stage dialog.
 * @param props - {@link AddToPipelineDialogContainerProps}
 * @param props.isOpen - Whether the dialog is open
 * @param props.onClose - Close request handler
 * @param props.playerId - Player to add
 * @param props.playerName - Optional player display name
 * @param props.boardId - Optional pre-selected board (hides the board picker)
 * @param props.onAdded - Invoked after a successful add
 * @returns The wired {@link AddToPipelineDialogView}, or nothing while closed.
 */
const AddToPipelineDialogContainer = ({
  isOpen,
  onClose,
  playerId,
  playerName,
  boardId,
  onAdded,
}: AddToPipelineDialogContainerProps) => {
  const showBoardPicker = !boardId;

  const [selectedBoardId, setSelectedBoardId] = useState<string>(boardId ?? "");
  const [selectedStageId, setSelectedStageId] = useState<string>("");

  const { data: boardsData, loading: boardsLoading } =
    useAddToPipelineDialogBoardsQuery({
      variables: { first: BOARDS_PAGE_SIZE },
      // SE-8818: only an OPEN profile (no board) dialog needs the board list.
      // Fetching while closed put this read into every player-profile batch.
      skip: !isOpen || !showBoardPicker,
      // Fetched on open, not on mount, so a cache-first read would keep the
      // first open's list for the session. Show the cached list at once and
      // refresh it behind.
      fetchPolicy: "cache-and-network",
    });

  const { data: phasesData, loading: phasesLoading } = useListKanbanPhasesQuery(
    {
      variables: { kanbanBoardId: selectedBoardId },
      skip: !isOpen || !selectedBoardId,
    }
  );

  const { addToPipeline, isAdding } = useAddPlayerToPipeline();

  /** Pipeline boards only — ROSTER / WATCHLIST boards are not add-to targets. */
  const boards = useMemo<readonly PipelineBoardOption[]>(
    () =>
      (boardsData?.listMyKanbanBoards.edges ?? [])
        .map(edge => edge.node)
        .filter(node => node.sourceType === KanbanSourceType.Pipeline)
        .map(node => ({ id: node.id, name: node.name })),
    [boardsData]
  );

  /** Stages for the selected board, in board (position) order. */
  const stages = useMemo<readonly PipelineStageOption[]>(
    () =>
      [...(phasesData?.listKanbanPhases ?? [])]
        .sort((a, b) => a.position - b.position)
        .map(phase => ({
          id: phase.id,
          name: phase.name,
          isTerminal: phase.isTerminal,
        })),
    [phasesData]
  );

  const handleBoardChange = useCallback((nextBoardId: string) => {
    setSelectedBoardId(nextBoardId);
    // A stage id only makes sense within one board — clear it on board change.
    setSelectedStageId("");
  }, []);

  const handleStageChange = useCallback(
    (nextStageId: string) => setSelectedStageId(nextStageId),
    []
  );

  const canConfirm = Boolean(selectedBoardId && selectedStageId && !isAdding);

  const handleConfirm = useCallback(async () => {
    if (!selectedBoardId || !selectedStageId) return;
    try {
      await addToPipeline({
        kanbanBoardId: selectedBoardId,
        kanbanPhaseId: selectedStageId,
        playerId,
      });
      // Clear the stage so a reopened dialog does not start pre-selected.
      setSelectedStageId("");
      onAdded?.();
      onClose();
    } catch {
      // Error toast already surfaced by the hook; keep the dialog open so the
      // manager can pick a different stage (e.g. after TERMINAL_ENTRY_NOT_ALLOWED).
    }
  }, [
    addToPipeline,
    onAdded,
    onClose,
    playerId,
    selectedBoardId,
    selectedStageId,
  ]);

  // Closed: every read above is skipped; render no modal either.
  if (!isOpen) return null;

  return (
    <AddToPipelineDialogView
      isOpen={isOpen}
      onClose={onClose}
      title="Add to pipeline"
      playerName={playerName}
      showBoardPicker={showBoardPicker}
      boards={boards}
      // A background refresh must not disable a picker that already has boards.
      boardsLoading={boardsLoading && !boardsData}
      selectedBoardId={selectedBoardId}
      onBoardChange={handleBoardChange}
      stages={stages}
      stagesLoading={phasesLoading}
      selectedStageId={selectedStageId}
      onStageChange={handleStageChange}
      isAdding={isAdding}
      canConfirm={canConfirm}
      onConfirm={handleConfirm}
    />
  );
};

AddToPipelineDialogContainer.displayName = "AddToPipelineDialogContainer";

export default memo(AddToPipelineDialogContainer);
